import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  ArrowUpRight, Heart, Download, ShoppingBag, Users, Sparkles,
  MessageCircle, Truck, Wallet, TrendingUp, MapPin, CheckCircle2,
} from "lucide-react";

const PASOS = [
  { n: "01", t: "Regístrate como NENI", d: "En 2 min tienes acceso al catálogo privado de todas las plazas afiliadas.",
    img: "https://images.unsplash.com/photo-1522542550221-31fd19575a2d?w=1200&q=80" },
  { n: "02", t: "Elige productos", d: "Descarga fotos, descripciones y define tu precio final. Tu margen = tu ganancia.",
    img: "https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1200&q=80" },
  { n: "03", t: "Publica en Facebook / WhatsApp", d: "Comparte los productos en tus redes con tus propias fotos y precios.",
    img: "https://images.unsplash.com/photo-1611262588024-d12430b98920?w=1200&q=80" },
  { n: "04", t: "Cita al cliente", d: "Acuerda punto de entrega dentro del radio de cobertura de la plaza.",
    img: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200&q=80" },
  { n: "05", t: "Repartidor entrega", d: "Un repartidor del mall lleva el producto al punto acordado. Trae recibo digital.",
    img: "https://images.unsplash.com/photo-1615461066841-6116e61058f4?w=1200&q=80" },
  { n: "06", t: "Cobras en efectivo", d: "Recibes el dinero del cliente y liquidas al repartidor el precio base del comercio.",
    img: "https://images.unsplash.com/photo-1580519542036-c47de6196ba5?w=1200&q=80" },
];

const BENEFICIOS = [
  { Icon: Wallet, t: "Sin inventario propio", d: "No inviertes en mercancía. La tienda tiene el stock, tú vendes." },
  { Icon: TrendingUp, t: "Tú fijas tu precio", d: "El margen entre el precio base y el que negocies con tu cliente es tuyo." },
  { Icon: Truck, t: "Logística incluida", d: "El repartidor del mall entrega el producto. Tú solo cierras la venta." },
  { Icon: Users, t: "Vende sin salir de casa", d: "Cierras ventas por WhatsApp, Facebook, Instagram o TikTok." },
];

export default function NenisPlaceLanding() {
  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <Navbar transparent />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-1/3 w-[600px] h-[600px] bg-[#FF3366]/8 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#D4FF00]/25 rounded-full blur-[120px]" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-14 pb-20">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-flex items-center gap-2 overline text-zinc-600 bg-white border border-zinc-200 px-3 py-1.5 rounded-full">
              <Sparkles className="w-3 h-3 text-[#FF3366]" /> Nuevo · Solo por invitación
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-6 font-display font-black text-6xl lg:text-[100px] leading-[0.88] tracking-tighter"
          >
            NENIS<br />
            <span className="inline-block bg-[#FF3366] text-white px-4 -rotate-1">PLACE.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-6 text-lg lg:text-2xl text-zinc-700 max-w-2xl font-light leading-snug"
          >
            El dropshipping <span className="font-medium text-zinc-900">hiperlocal</span> hecho en México.<br />
            Vende ropa, zapatos y artículos de los mejores locales del mall <span className="italic text-[#FF3366]">sin comprar inventario.</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <Link to="/register?role=neni" data-testid="nenis-cta-register">
              <Button className="h-12 px-6 rounded-sm bg-[#FF3366] hover:bg-[#E62958] text-white font-medium">
                Quiero ser NENI <ArrowUpRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <a href="#como-funciona">
              <Button variant="outline" className="h-12 px-6 rounded-sm border-zinc-300">Cómo funciona</Button>
            </a>
            <div className="ml-2 text-xs text-zinc-500">Registro gratis · Sin inventario · Cobras en efectivo</div>
          </motion.div>

          {/* Benefit tiles */}
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {BENEFICIOS.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
                className="bg-white border border-zinc-200 rounded-sm p-5"
              >
                <b.Icon className="w-6 h-6 text-[#FF3366]" />
                <div className="mt-3 font-display font-bold tracking-tight">{b.t}</div>
                <p className="text-xs text-zinc-600 mt-1 leading-relaxed">{b.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Proceso 6 pasos */}
      <section id="como-funciona" className="bg-white border-y border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="overline text-zinc-500 mb-2">Cómo funciona</div>
          <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tighter max-w-3xl">
            6 pasos para ganar tu <span className="italic text-[#FF3366]">primera comisión.</span>
          </h2>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PASOS.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }}
                className="relative overflow-hidden rounded-sm bg-zinc-900 text-white card-lift min-h-[320px] flex flex-col justify-end"
                data-testid={`paso-${i}`}
              >
                <img src={p.img} alt={p.t} className="absolute inset-0 w-full h-full object-cover opacity-55" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
                <div className="relative p-6 z-10">
                  <div className="font-display font-black text-6xl tracking-tighter text-white/25 leading-none">{p.n}</div>
                  <div className="mt-2 font-display font-bold text-xl tracking-tight">{p.t}</div>
                  <p className="mt-2 text-sm text-white/85 leading-relaxed">{p.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Modelo económico */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-zinc-900 text-white rounded-sm p-10">
            <div className="overline text-white/50">Ejemplo real</div>
            <div className="mt-3 font-display font-black text-3xl tracking-tighter">Vendes un tenis</div>
            <ul className="mt-6 space-y-3 text-sm">
              <li className="flex justify-between border-b border-white/10 pb-3"><span className="text-white/60">Precio base del comercio</span> <span className="font-mono font-bold">$500</span></li>
              <li className="flex justify-between border-b border-white/10 pb-3"><span className="text-white/60">Precio que negocias con tu cliente</span> <span className="font-mono font-bold">$800</span></li>
              <li className="flex justify-between text-[#D4FF00]"><span className="font-medium">Tu ganancia neta</span> <span className="font-mono font-black text-xl">$300</span></li>
            </ul>
          </div>
          <div className="bg-[#D4FF00] rounded-sm p-10">
            <Heart className="w-10 h-10" />
            <div className="mt-4 font-display font-black text-3xl tracking-tighter">¿Por qué NENIS PLACE?</div>
            <ul className="mt-4 space-y-2 text-sm font-medium">
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> Cero inversión inicial</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> Sin límite de ganancias</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> El mall entrega por ti</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> Solo cobras en efectivo, seguro</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20">
        <div className="relative overflow-hidden rounded-sm bg-gradient-to-br from-[#FF3366] to-[#E62958] text-white p-12 lg:p-16">
          <ShoppingBag className="w-12 h-12 opacity-40" />
          <h2 className="mt-6 font-display font-black text-4xl lg:text-6xl tracking-tighter max-w-2xl">
            Empieza a vender hoy.<br />Sin invertir un peso.
          </h2>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/register?role=neni">
              <Button className="h-12 px-6 rounded-sm bg-white text-[#FF3366] hover:bg-zinc-100 font-medium">
                Registrarme como NENI <ArrowUpRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="h-12 px-6 rounded-sm bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white">
                Ya soy NENI, entrar
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 text-xs text-zinc-500 text-center">
          NENIS PLACE by en 90 MINUTOS · Vende como emprendedor sin invertir
        </div>
      </footer>
    </div>
  );
}
